import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../fetchInterceptor';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Upload, FileText, Loader2, CheckCircle2, XCircle, Inbox, Folder, X, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPACE_LABELS = {
  firm: 'Organization', personal: 'My Files', client: 'Clients',
  vendor: 'Vendors', prospect: 'Prospects', custom: 'Custom',
};

export default function DocumentUploadModal({ open, onClose, workspaceId, defaultFolderId = null, onUploaded }) {
  const [folders, setFolders] = useState([]);
  const [dest, setDest] = useState('inbox'); // 'inbox' | folder_id
  const [filter, setFilter] = useState('');
  const [files, setFiles] = useState([]); // [{ file, status: 'ready'|'uploading'|'done'|'error', error }]
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDest(defaultFolderId || 'inbox');
      setFiles([]);
      setFilter('');
    }
  }, [open, defaultFolderId]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/folders/search?limit=200`);
        if (r.ok) setFolders(await r.json());
      } catch { /* picker degrades to inbox-only */ }
    })();
  }, [open, workspaceId]);

  const addFiles = (list) => {
    const incoming = Array.from(list || []).filter(f => f.size <= 50 * 1024 * 1024);
    const rejected = Array.from(list || []).length - incoming.length;
    if (rejected > 0) toast.error(`${rejected} file(s) over the 50MB limit were skipped`);
    setFiles(prev => [...prev, ...incoming.map(f => ({ file: f, status: 'ready', error: null }))]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!files.length) return;
    setBusy(true);
    let ok = 0, fail = 0, scanned = 0;
    const SCANNABLE = ['.pdf', '.png', '.jpg', '.jpeg'];
    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'done') continue;
      setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'uploading' } : f));
      const name = files[i].file.name.toLowerCase();
      const isScannable = SCANNABLE.some(ext => name.endsWith(ext));
      try {
        let r;
        if (dest === 'inbox' && isScannable) {
          // AI Buck auto-file: run through the OCR pipeline — detects the doc
          // type (invoice, receipt, statement, tax form…) and files it into
          // the right folder automatically.
          const fd = new FormData();
          fd.append('file', files[i].file);
          fd.append('document_type', 'auto');
          fd.append('workspace_id', workspaceId);
          r = await apiFetch(`${API_URL}/api/ocr/upload`, { method: 'POST', body: fd });
          if (r.ok) scanned++;
          if (!r.ok && (r.status === 402 || r.status === 400)) {
            // AI plan gate or unsupported — fall back to plain inbox upload
            const fd2 = new FormData();
            fd2.append('file', files[i].file);
            r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/document-inbox/upload`, { method: 'POST', body: fd2 });
          }
        } else {
          const fd = new FormData();
          fd.append('file', files[i].file);
          let url;
          if (dest === 'inbox') {
            url = `${API_URL}/api/workspaces/${workspaceId}/document-inbox/upload`;
          } else {
            fd.append('folder_id', dest);
            url = `${API_URL}/api/workspaces/${workspaceId}/documents/upload`;
          }
          r = await apiFetch(url, { method: 'POST', body: fd });
        }
        if (r.ok) {
          ok++;
          setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'done' } : f));
        } else {
          const err = await r.json().catch(() => ({}));
          fail++;
          const detail = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Upload failed');
          setFiles(prev => prev.map((f, j) => j === i
            ? { ...f, status: 'error', error: detail }
            : f));
        }
      } catch {
        fail++;
        setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'error', error: 'Network error' } : f));
      }
    }
    setBusy(false);
    if (ok > 0) {
      if (scanned > 0) {
        toast.success(`AI Buck is reading ${scanned} file${scanned === 1 ? '' : 's'} — they'll be auto-filed shortly. Watch the bell for updates.`);
      } else {
        toast.success(`${ok} file${ok === 1 ? '' : 's'} uploaded${fail ? ` · ${fail} failed` : ''}`);
      }
      onUploaded?.();
      if (fail === 0) onClose?.();
    } else if (fail > 0) {
      toast.error('Upload failed');
    }
  };

  const selectedFolder = folders.find(f => f.folder_id === dest);
  const q = filter.trim().toLowerCase();
  const visibleFolders = (q
    ? folders.filter(f => (f.path || f.name || '').toLowerCase().includes(q))
    : folders
  ).slice(0, 100);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent className="sm:max-w-[560px]" data-testid="document-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-500" /> Upload documents
          </DialogTitle>
          <DialogDescription>
            Drop files below, then choose where they should go.
          </DialogDescription>
        </DialogHeader>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
            dragOver ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-emerald-500/50 hover:bg-muted/40',
          )}
          data-testid="upload-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            data-testid="upload-file-input"
          />
          <Upload className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">Drag & drop files here, or click to browse</p>
          <p className="text-xs text-muted-foreground mt-1">PDF, images, spreadsheets, docs · up to 50MB each</p>
        </div>

        {/* Selected files */}
        {files.length > 0 && (
          <div className="max-h-36 overflow-y-auto space-y-1.5" data-testid="upload-file-list">
            {files.map((f, i) => (
              <div key={`${f.file.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-1.5 text-sm">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate flex-1">{f.file.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{(f.file.size / 1024 / 1024).toFixed(1)}MB</span>
                {f.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-emerald-500 shrink-0" />}
                {f.status === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                {f.status === 'error' && (
                  <span className="flex items-center gap-1 text-red-500 text-[10px] shrink-0" title={f.error}>
                    <XCircle className="w-4 h-4" /> {f.error}
                  </span>
                )}
                {f.status === 'ready' && !busy && (
                  <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`upload-remove-file-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Destination picker */}
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Destination</p>
          <button
            onClick={() => setDest('inbox')}
            className={cn(
              'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
              dest === 'inbox' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:bg-muted/40',
            )}
            data-testid="upload-dest-inbox"
          >
            <Inbox className="w-4 h-4 text-emerald-500 shrink-0" />
            <span className="flex-1">
              <span className="font-medium">Let AI Buck read &amp; file it <span className="text-emerald-600 dark:text-emerald-400">(recommended)</span></span>
              <span className="block text-[11px] text-muted-foreground">Detects invoices, receipts, statements &amp; tax docs — files each into the right folder automatically</span>
            </span>
            {dest === 'inbox' && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
          </button>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search folders…"
              className="pl-8 h-8 text-sm"
              data-testid="upload-folder-filter"
            />
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="upload-folder-list">
            {visibleFolders.length === 0 ? (
              <p className="text-xs text-muted-foreground px-3 py-3 text-center">No folders match</p>
            ) : visibleFolders.map(f => (
              <button
                key={f.folder_id}
                onClick={() => setDest(f.folder_id)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                  dest === f.folder_id ? 'bg-emerald-500/10' : 'hover:bg-muted/40',
                )}
                data-testid={`upload-dest-folder-${f.folder_id}`}
              >
                <Folder className="w-3.5 h-3.5 shrink-0" style={{ color: f.color || '#3b82f6' }} />
                <span className="truncate flex-1">{f.path || f.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{SPACE_LABELS[f.space] || f.space}</span>
                {dest === f.folder_id && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter className="flex items-center gap-2">
          <p className="text-[11px] text-muted-foreground mr-auto truncate">
            {dest === 'inbox'
              ? 'AI Buck will read & auto-file these'
              : `Files go to: ${selectedFolder?.path || selectedFolder?.name || 'selected folder'}`}
          </p>
          <Button variant="ghost" onClick={() => !busy && onClose?.()} disabled={busy} data-testid="upload-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={busy || files.filter(f => f.status !== 'done').length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
            data-testid="upload-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload {files.filter(f => f.status !== 'done').length || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
